import React, { useState, useRef, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

export default function HupalupaGoKalamisFestival() {
  const [exporting, setExporting] = useState(false);
  const captureRef = useRef(null);

  const handleExportPDF = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    const swapped = [];
    try {
      const el = captureRef.current;
      if (!el) return;

      // Tum gorseller yuklenene kadar bekle (logo/ikonlar bos cikmasin)
      const imgs = Array.from(el.querySelectorAll('img'));
      await Promise.all(
        imgs.map(img => {
          if (img.complete && img.naturalWidth > 0) return Promise.resolve();
          return new Promise(resolve => {
            const done = () => resolve();
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
          });
        })
      );

      // SVG / WebP logolari PNG data-url'e rasterize et — html2canvas bu formatlari bozuyor
      for (const img of imgs) {
        const src = img.getAttribute('src') || '';
        if (src.startsWith('data:')) continue;
        try {
          const rect = img.getBoundingClientRect();
          const w = Math.max(1, Math.round(rect.width));
          const h = Math.max(1, Math.round(rect.height));
          const s = 3; // keskinlik icin 3x
          const c = document.createElement('canvas');
          c.width = w * s;
          c.height = h * s;
          c.getContext('2d').drawImage(img, 0, 0, w * s, h * s);
          const dataUrl = c.toDataURL('image/png'); // cross-origin gorselde burada throw eder
          swapped.push({ img, src, w: img.style.width, h: img.style.height });
          img.style.width = w + 'px';
          img.style.height = h + 'px';
          img.src = dataUrl;
        } catch (e) {
          // cross-origin / cizilemeyen gorsel: orijinali oldugu gibi birak
        }
      }

      // Yeni data-url gorseller decode olana kadar bekle
      await Promise.all(
        imgs.map(img => (img.complete && img.naturalWidth > 0)
          ? Promise.resolve()
          : new Promise(resolve => { img.onload = resolve; img.onerror = resolve; }))
      );

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1280,
        letterRendering: true,
        allowTaint: true,
        imageTimeout: 15000,
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;
      const pxPerMm = canvas.width / usableW;
      const pageHpx = usableH * pxPerMm;

      // Tam sayfa: tek uzun canvas'i A4 dilimlerine bol
      let posY = 0;
      let pageIndex = 0;
      while (posY < canvas.height) {
        if (pageIndex > 0) pdf.addPage();
        const sliceH = Math.min(pageHpx, canvas.height - posY);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceH;
        const ctx = sliceCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, -posY);
        const imgData = sliceCanvas.toDataURL('image/jpeg', 0.92);
        const drawH = sliceH / pxPerMm;
        pdf.addImage(imgData, 'JPEG', margin, margin, usableW, drawH);
        posY += pageHpx;
        pageIndex++;
      }

      pdf.save('Hupalupa_Go_Kalamis_Festivali_Medya_Plani.pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('PDF export sirasinda hata olustu. Konsolu kontrol et.');
    } finally {
      // Rasterize edilen logolari orijinal src'ye geri yukle
      for (const s of swapped) {
        s.img.src = s.src;
        s.img.style.width = s.w;
        s.img.style.height = s.h;
      }
      setExporting(false);
    }
  }, [exporting]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">

      {/* PDF EXPORT BUTTON — capture disinda, PDF'e girmez */}
      <div className="flex justify-end">
        <button
          onClick={handleExportPDF}
          disabled={exporting}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition ${exporting ? 'bg-gray-100 text-gray-400 cursor-wait border-gray-200' : 'bg-gray-800 text-white hover:bg-gray-700 border-gray-800'}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {exporting ? 'PDF Hazirlaniyor…' : 'PDF olarak indir'}
        </button>
      </div>

      <div ref={captureRef} className="space-y-10">

      {/* ======================================================================= */}
      {/* HUPALUPA GO - KALAMIS FESTIVALI - Medya Plani                          */}
      {/* Hedef: 3.000 - 4.500 bilet                                             */}
      {/* ======================================================================= */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="bg-white border-b p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="rounded-lg px-3 py-1.5">
              <img src="https://hupalupa.com.tr/images/logo_s.png" alt="Hupalupa" className="h-9 w-auto object-contain" />
            </div>
            <div className="ml-1">
              <h2 className="text-lg font-bold text-gray-800">HUPALUPA GO - Kalamis Festivali Medya Plani <span className="ml-1 text-xs font-semibold text-amber-600 align-middle">(Revize 2)</span></h2>
              <p className="text-sm text-gray-500">Hedef: 3.000 - 4.500 bilet • Aile odakli, full-funnel kampanya yapisi</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full border border-gray-200">
              10 gun (1-10 Haziran) • Festival 13-14 Haziran 2026
            </span>
            <span className="inline-flex items-center px-3 py-1.5 bg-gray-800 text-white text-sm font-semibold rounded-full">
              Butce: &#8378;750.000
            </span>
          </div>
        </div>

        <div className="p-6 space-y-8">

          {/* -- 1. EXECUTIVE SUMMARY -------------------------------------------- */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">1. Genel Hedef ve Butce Ozeti</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <span className="text-xs font-medium text-gray-700">Hedef</span>
                <p className="text-xl font-bold text-gray-800">3K - 4.5K</p>
                <p className="text-[10px] text-gray-600 mt-0.5">satilan bilet</p>
              </div>
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
                <span className="text-xs font-medium text-emerald-700">Butce</span>
                <p className="text-xl font-bold text-emerald-800">&#8378;750.000</p>
                <p className="text-[10px] text-emerald-600 mt-0.5">Tek dalga: 1-10 Haziran</p>
              </div>
              <div className="rounded-xl border border-violet-100 bg-violet-50 p-4">
                <span className="text-xs font-medium text-violet-700">CAC Hedefi</span>
                <p className="text-xl font-bold text-violet-800">&#8378;200</p>
                <p className="text-[10px] text-violet-600 mt-0.5">bilet basina dijital maliyet</p>
              </div>
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                <span className="text-xs font-medium text-blue-700">Toplam Erisim</span>
                <p className="text-xl font-bold text-blue-800">~4M</p>
                <p className="text-[10px] text-blue-600 mt-0.5">tekil kisi (kumulatif)</p>
              </div>
              <div className="rounded-xl border border-orange-100 bg-orange-50 p-4">
                <span className="text-xs font-medium text-orange-700">Bilet Sayfasi Trafigi</span>
                <p className="text-xl font-bold text-orange-800">~120K</p>
                <p className="text-[10px] text-orange-600 mt-0.5">donusum odakli tiklama</p>
              </div>
              <div className="rounded-xl border border-pink-100 bg-pink-50 p-4">
                <span className="text-xs font-medium text-pink-700">Conversion Rate</span>
                <p className="text-xl font-bold text-pink-800">3.1%</p>
                <p className="text-[10px] text-pink-600 mt-0.5">click - bilet (lower funnel)</p>
              </div>
            </div>
            <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700 leading-relaxed">
              <span className="font-semibold">Butce mantigi:</span> Hedef bilet x hedef CAC = butce. 3.750 bilet (3K-4.5K bandinin ortasi) x <span className="font-semibold">&#8378;200 CAC</span> = <span className="font-semibold">&#8378;750.000</span>.
              &#8378;200 CAC, &#8378;1.250'lik gunluk bilet icin saglikli bir akuzisyon maliyeti (LTV/CAC yaklasik 6).
              Tum butce <span className="font-semibold">Haziran'in ilk 10 gununde (1-10 Haziran)</span>, festivalden hemen once harcanir.
              10 gunluk yogun pencerede kurgu <span className="font-semibold">conversion-heavy</span> olmali: ilk gunler hizli awareness + sicak kitle olusturma,
              son gunler coklu retargeting ve son cagri.
            </div>
          </section>

          {/* -- 2. FUNNEL & BUDGET BREAKDOWN ------------------------------------ */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">2. Full-Funnel Butce Dagilimi</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Upper Funnel */}
              <div className="rounded-xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex items-center px-2.5 py-1 bg-purple-100 text-purple-800 text-xs font-bold rounded-full">UPPER FUNNEL</span>
                </div>
                <h4 className="text-base font-bold text-gray-800 mb-1">Awareness & Reach</h4>
                <p className="text-[11px] text-gray-500 mb-3">"Festival var, gel" - dikkat cekme asamasi</p>
                <p className="text-2xl font-bold text-purple-700">&#8378;235.000</p>
                <p className="text-xs text-purple-600 mt-1">Butcenin %31'i • Agirlik 1-4 Haziran</p>
                <ul className="mt-3 text-[11px] text-gray-700 space-y-1.5">
                  <li className="flex gap-1.5"><span className="text-purple-500">•</span><span>Meta Reach & Frequency</span></li>
                  <li className="flex gap-1.5"><span className="text-purple-500">•</span><span>YouTube TrueView + CTV</span></li>
                  <li className="flex gap-1.5"><span className="text-purple-500">•</span><span>TikTok In-Feed Video Views</span></li>
                  <li className="flex gap-1.5"><span className="text-purple-500">•</span><span>Programmatic display + video</span></li>
                </ul>
                <div className="mt-3 pt-3 border-t border-purple-100">
                  <p className="text-[10px] font-semibold text-purple-700 uppercase">KPI</p>
                  <p className="text-[11px] text-gray-700 mt-0.5">Reach: 2.5M+ • Video Completion %25+ • CPM &#8378;16-25</p>
                </div>
              </div>

              {/* Mid Funnel */}
              <div className="rounded-xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex items-center px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-full">MID FUNNEL</span>
                </div>
                <h4 className="text-base font-bold text-gray-800 mb-1">Consideration & Engagement</h4>
                <p className="text-[11px] text-gray-500 mb-3">"Icinde ne var? Ailece eglenir miyiz?"</p>
                <p className="text-2xl font-bold text-amber-700">&#8378;195.000</p>
                <p className="text-xs text-amber-600 mt-1">Butcenin %26'si • 4-7 Haziran</p>
                <ul className="mt-3 text-[11px] text-gray-700 space-y-1.5">
                  <li className="flex gap-1.5"><span className="text-amber-500">•</span><span>Meta Engagement & Traffic</span></li>
                  <li className="flex gap-1.5"><span className="text-amber-500">•</span><span>Google Demand Gen</span></li>
                  <li className="flex gap-1.5"><span className="text-amber-500">•</span><span>Meta Video Views</span></li>
                  <li className="flex gap-1.5"><span className="text-amber-500">•</span><span>Instagram Story Ads</span></li>
                </ul>
                <div className="mt-3 pt-3 border-t border-amber-100">
                  <p className="text-[10px] font-semibold text-amber-700 uppercase">KPI</p>
                  <p className="text-[11px] text-gray-700 mt-0.5">Engagement Rate %3.5+ • CPC &#8378;2.5-4 • Avg. Time on Site 90sn+</p>
                </div>
              </div>

              {/* Lower Funnel */}
              <div className="rounded-xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex items-center px-2.5 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full">LOWER FUNNEL</span>
                </div>
                <h4 className="text-base font-bold text-gray-800 mb-1">Conversion (Bilet Satisi)</h4>
                <p className="text-[11px] text-gray-500 mb-3">"Simdi al, son N gunde fiyat artar"</p>
                <p className="text-2xl font-bold text-emerald-700">&#8378;320.000</p>
                <p className="text-xs text-emerald-600 mt-1">Butcenin %43'u • 7-10 Haziran</p>
                <ul className="mt-3 text-[11px] text-gray-700 space-y-1.5">
                  <li className="flex gap-1.5"><span className="text-emerald-500">•</span><span>Google Search</span></li>
                  <li className="flex gap-1.5"><span className="text-emerald-500">•</span><span>Google PMAX</span></li>
                  <li className="flex gap-1.5"><span className="text-emerald-500">•</span><span>Meta Sales / Conversion</span></li>
                  <li className="flex gap-1.5"><span className="text-emerald-500">•</span><span>Google GDN + Meta Retargeting</span></li>
                </ul>
                <div className="mt-3 pt-3 border-t border-emerald-100">
                  <p className="text-[10px] font-semibold text-emerald-700 uppercase">KPI</p>
                  <p className="text-[11px] text-gray-700 mt-0.5">CVR %3+ • CAC &#8378;200/bilet • Hedef: 3.750 bilet</p>
                </div>
              </div>
            </div>
          </section>

          {/* -- 3. PHASE-BY-PHASE TIMELINE ------------------------------------- */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">3. Iletisim Takvimi (10 gunluk lansman, 1-10 Haziran • Festival 13-14 Haziran)</h3>
            <div className="rounded-xl border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Faz</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zaman</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mesaj / Hook</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktif Funnel</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Butce Payi</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    <tr className="bg-purple-50/40">
                      <td className="px-4 py-3 text-sm font-semibold text-purple-800">Faz 1 - Teaser / Awareness</td>
                      <td className="px-4 py-3 text-xs text-gray-700">1 Haz - 3 Haz (3 gun)</td>
                      <td className="px-4 py-3 text-xs text-gray-700">"Kalamis'ta ailece muhtesem bir gun geliyor."<br/><span className="text-gray-500">Lokasyon, atmosfer, anisal kareler. Bilet yok.</span></td>
                      <td className="px-4 py-3 text-xs"><span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 rounded">Upper</span></td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;150.000 (%20)</td>
                    </tr>
                    <tr className="bg-amber-50/40">
                      <td className="px-4 py-3 text-sm font-semibold text-amber-800">Faz 2 - Lineup & Icerik</td>
                      <td className="px-4 py-3 text-xs text-gray-700">4 Haz - 5 Haz (2 gun)</td>
                      <td className="px-4 py-3 text-xs text-gray-700">"Cocuk atolyeleri, sahne, food court, surprizler."<br/><span className="text-gray-500">Aktivite carousel'leri, behind-the-scenes Reels. Erken bilet teasers.</span></td>
                      <td className="px-4 py-3 text-xs">
                        <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 rounded mr-1">Upper</span>
                        <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Mid</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;175.000 (%23)</td>
                    </tr>
                    <tr className="bg-emerald-50/40">
                      <td className="px-4 py-3 text-sm font-semibold text-emerald-800">Faz 3 - Erken Bilet</td>
                      <td className="px-4 py-3 text-xs text-gray-700">6 Haz - 8 Haz (3 gun)</td>
                      <td className="px-4 py-3 text-xs text-gray-700">"Erken bilet & 2 gunluk aile paketi indirimi."<br/><span className="text-gray-500">Net fayda + zaman baskisi. Aile bilet paketi vurgusu.</span></td>
                      <td className="px-4 py-3 text-xs">
                        <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 rounded mr-1">Mid</span>
                        <span className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">Lower</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;210.000 (%28)</td>
                    </tr>
                    <tr className="bg-rose-50/40">
                      <td className="px-4 py-3 text-sm font-semibold text-rose-800">Faz 4 - Son Cagri</td>
                      <td className="px-4 py-3 text-xs text-gray-700">9 Haz - 10 Haz (2 gun)</td>
                      <td className="px-4 py-3 text-xs text-gray-700">"Son biletler! Hafta sonu Kalamis'tasin."<br/><span className="text-gray-500">Retargeting heavy + countdown + sinirlilik.</span></td>
                      <td className="px-4 py-3 text-xs"><span className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">Lower</span></td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;215.000 (%29)</td>
                    </tr>
                    <tr className="bg-gray-800 text-white font-bold">
                      <td className="px-4 py-3 text-sm" colSpan={4}>Genel Toplam (1-10 Haziran)</td>
                      <td className="px-4 py-3 text-sm text-right">&#8378;750.000 (%100)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* -- 4. PLATFORM / CAMPAIGN BREAKDOWN ------------------------------- */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">4. Platform ve Kampanya Detayi</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-5">
              <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <img src="/6033716.png" alt="Meta" className="w-5 h-5 object-contain" />
                  <span className="text-xs font-medium text-indigo-700">Meta Ads</span>
                </div>
                <p className="text-xl font-bold text-indigo-800">&#8378;360.000</p>
                <p className="text-[10px] text-indigo-600 mt-0.5">%48 • Reach + Engagement + Sales</p>
              </div>
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <img src="/google_ads-512.webp" alt="Google" className="w-5 h-5 object-contain" />
                  <span className="text-xs font-medium text-blue-700">Google Ads</span>
                </div>
                <p className="text-xl font-bold text-blue-800">&#8378;200.000</p>
                <p className="text-[10px] text-blue-600 mt-0.5">%27 • Demand Gen + GDN agirlikli</p>
              </div>
              <div className="rounded-xl border border-red-100 bg-red-50 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <img src="/youtubelogov2.svg" alt="YouTube" className="w-4 h-4 object-contain" />
                  <span className="text-xs font-medium text-red-700">YouTube / CTV</span>
                </div>
                <p className="text-xl font-bold text-red-800">&#8378;120.000</p>
                <p className="text-[10px] text-red-600 mt-0.5">%16 • TrueView + CTV reach</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <img src="/tiktokv4.svg" alt="TikTok" className="w-5 h-5 object-contain" />
                  <span className="text-xs font-medium text-gray-700">TikTok Ads</span>
                </div>
                <p className="text-xl font-bold text-gray-800">&#8378;50.000</p>
                <p className="text-[10px] text-gray-600 mt-0.5">%7 • Video Views + Traffic</p>
              </div>
              <div className="rounded-xl border border-teal-100 bg-teal-50 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-teal-700">Programmatic</span>
                </div>
                <p className="text-xl font-bold text-teal-800">&#8378;20.000</p>
                <p className="text-[10px] text-teal-600 mt-0.5">%3 • Dusuk butceli reach + native</p>
              </div>
            </div>

            <div className="rounded-xl border overflow-hidden">
              <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Kampanya Plani - Platform x Funnel</div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kampanya Tipi</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Funnel</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reach</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Click / View</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPC / CPM</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Butce</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {/* META ADS */}
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium" rowSpan={5}>
                        <div className="flex items-center gap-2"><img src="/6033716.png" alt="Meta" className="w-5 h-5" /><span>Meta Ads</span></div>
                      </td>
                      <td className="px-4 py-3 text-sm">Reach & Frequency (FB+IG Reels)</td>
                      <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">Upper</span></td>
                      <td className="px-4 py-3 text-sm text-right">2.000.000</td>
                      <td className="px-4 py-3 text-sm text-right">~2.95M impressions</td>
                      <td className="px-4 py-3 text-sm text-right">CPM &#8378;22</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;65.000</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">Engagement (Carousel: aktiviteler)</td>
                      <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Mid</span></td>
                      <td className="px-4 py-3 text-sm text-right">-</td>
                      <td className="px-4 py-3 text-sm text-right">20.000 click</td>
                      <td className="px-4 py-3 text-sm text-right">CPC &#8378;3.5</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;70.000</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">Video Views (Aile vlog 15sn)</td>
                      <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Mid</span></td>
                      <td className="px-4 py-3 text-sm text-right">-</td>
                      <td className="px-4 py-3 text-sm text-right">450.000 views</td>
                      <td className="px-4 py-3 text-sm text-right">CPV &#8378;0.10</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;45.000</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">Sales / Conversions (Bilet)</td>
                      <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">Lower</span></td>
                      <td className="px-4 py-3 text-sm text-right">-</td>
                      <td className="px-4 py-3 text-sm text-right">40.000 click</td>
                      <td className="px-4 py-3 text-sm text-right">CPC &#8378;3.2</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;128.000</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">Retargeting (Siteyi ziyaret edip almayan)</td>
                      <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">Lower</span></td>
                      <td className="px-4 py-3 text-sm text-right">-</td>
                      <td className="px-4 py-3 text-sm text-right">18.500 click</td>
                      <td className="px-4 py-3 text-sm text-right">CPC &#8378;2.8</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;52.000</td>
                    </tr>
                    <tr className="bg-indigo-50 font-semibold">
                      <td className="px-4 py-3 text-sm text-indigo-800">Meta Total</td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-sm text-right text-indigo-800">2.000.000</td>
                      <td className="px-4 py-3 text-sm text-right text-indigo-800">78.500 click + 450K views</td>
                      <td className="px-4 py-3 text-sm text-right text-indigo-800">-</td>
                      <td className="px-4 py-3 text-sm text-right text-indigo-800">&#8378;360.000</td>
                    </tr>

                    {/* GOOGLE ADS */}
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium" rowSpan={4}>
                        <div className="flex items-center gap-2"><img src="/google_ads-512.webp" alt="Google" className="w-5 h-5" /><span>Google Ads</span></div>
                      </td>
                      <td className="px-4 py-3 text-sm">Search (brand + festival kaliplari)</td>
                      <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">Lower</span></td>
                      <td className="px-4 py-3 text-sm text-right">-</td>
                      <td className="px-4 py-3 text-sm text-right">9.200 click</td>
                      <td className="px-4 py-3 text-sm text-right">CPC &#8378;6.0</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;55.000</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">Performance Max (Bilet feed)</td>
                      <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">Lower</span></td>
                      <td className="px-4 py-3 text-sm text-right">-</td>
                      <td className="px-4 py-3 text-sm text-right">10.000 click</td>
                      <td className="px-4 py-3 text-sm text-right">CPC &#8378;4.5</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;45.000</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">Demand Gen (Discover + YT shorts)</td>
                      <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Mid</span></td>
                      <td className="px-4 py-3 text-sm text-right">-</td>
                      <td className="px-4 py-3 text-sm text-right">20.000 click</td>
                      <td className="px-4 py-3 text-sm text-right">CPC &#8378;3.0</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;60.000</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">GDN Display Retargeting</td>
                      <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">Lower</span></td>
                      <td className="px-4 py-3 text-sm text-right">1.800.000</td>
                      <td className="px-4 py-3 text-sm text-right">20.000 click</td>
                      <td className="px-4 py-3 text-sm text-right">CPC &#8378;2.0</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;40.000</td>
                    </tr>
                    <tr className="bg-blue-50 font-semibold">
                      <td className="px-4 py-3 text-sm text-blue-800">Google Total</td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-sm text-right text-blue-800">1.800.000</td>
                      <td className="px-4 py-3 text-sm text-right text-blue-800">59.200 click</td>
                      <td className="px-4 py-3 text-sm text-right text-blue-800">-</td>
                      <td className="px-4 py-3 text-sm text-right text-blue-800">&#8378;200.000</td>
                    </tr>

                    {/* YOUTUBE / CTV */}
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium" rowSpan={2}>
                        <div className="flex items-center gap-2"><img src="/youtubelogov2.svg" alt="YouTube" className="w-4 h-4" /><span>YouTube / CTV</span></div>
                      </td>
                      <td className="px-4 py-3 text-sm">TrueView (15sn skippable)</td>
                      <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">Upper</span></td>
                      <td className="px-4 py-3 text-sm text-right">1.350.000</td>
                      <td className="px-4 py-3 text-sm text-right">416.000 views</td>
                      <td className="px-4 py-3 text-sm text-right">CPV &#8378;0.18</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;75.000</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">CTV (Connected TV - Smart TV ailelere)</td>
                      <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">Upper</span></td>
                      <td className="px-4 py-3 text-sm text-right">580.000</td>
                      <td className="px-4 py-3 text-sm text-right">775K impressions</td>
                      <td className="px-4 py-3 text-sm text-right">CPM &#8378;58</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;45.000</td>
                    </tr>
                    <tr className="bg-red-50 font-semibold">
                      <td className="px-4 py-3 text-sm text-red-800">YouTube/CTV Total</td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-sm text-right text-red-800">1.850.000</td>
                      <td className="px-4 py-3 text-sm text-right text-red-800">416K views</td>
                      <td className="px-4 py-3 text-sm text-right text-red-800">-</td>
                      <td className="px-4 py-3 text-sm text-right text-red-800">&#8378;120.000</td>
                    </tr>

                    {/* TIKTOK */}
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium" rowSpan={2}>
                        <div className="flex items-center gap-2"><img src="/tiktokv4.svg" alt="TikTok" className="w-5 h-5" /><span>TikTok Ads</span></div>
                      </td>
                      <td className="px-4 py-3 text-sm">In-Feed Video Views</td>
                      <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">Upper</span></td>
                      <td className="px-4 py-3 text-sm text-right">300.000</td>
                      <td className="px-4 py-3 text-sm text-right">500.000 views</td>
                      <td className="px-4 py-3 text-sm text-right">CPV &#8378;0.06</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;30.000</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">Traffic / Web Conversion</td>
                      <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Mid</span></td>
                      <td className="px-4 py-3 text-sm text-right">-</td>
                      <td className="px-4 py-3 text-sm text-right">6.700 click</td>
                      <td className="px-4 py-3 text-sm text-right">CPC &#8378;3.0</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;20.000</td>
                    </tr>
                    <tr className="bg-gray-100 font-semibold">
                      <td className="px-4 py-3 text-sm text-gray-800">TikTok Total</td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-sm text-right text-gray-800">300.000</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-800">6.7K click + 500K views</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-800">-</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-800">&#8378;50.000</td>
                    </tr>

                    {/* PROGRAMATIK */}
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium">
                        <div className="flex items-center gap-2"><span>Programmatic</span></div>
                      </td>
                      <td className="px-4 py-3 text-sm">Display + Native (aile/parenting envanteri)</td>
                      <td className="px-4 py-3 text-xs">
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded mr-1">Upper</span>
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Mid</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">850.000</td>
                      <td className="px-4 py-3 text-sm text-right">1.25M impressions</td>
                      <td className="px-4 py-3 text-sm text-right">CPM &#8378;16</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">&#8378;20.000</td>
                    </tr>
                    <tr className="bg-teal-50 font-semibold">
                      <td className="px-4 py-3 text-sm text-teal-800">Programmatic Total</td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-sm text-right text-teal-800">850.000</td>
                      <td className="px-4 py-3 text-sm text-right text-teal-800">1.25M impressions</td>
                      <td className="px-4 py-3 text-sm text-right text-teal-800">-</td>
                      <td className="px-4 py-3 text-sm text-right text-teal-800">&#8378;20.000</td>
                    </tr>

                    {/* GRAND TOTAL */}
                    <tr className="bg-gray-800 text-white font-bold">
                      <td className="px-4 py-3 text-sm">Grand Total</td>
                      <td className="px-4 py-3" colSpan={2}></td>
                      <td className="px-4 py-3 text-sm text-right">~4M tekil reach</td>
                      <td className="px-4 py-3 text-sm text-right">144K click + 1.37M video</td>
                      <td className="px-4 py-3 text-sm text-right">-</td>
                      <td className="px-4 py-3 text-sm text-right">&#8378;750.000 (1-10 Haziran)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* -- 5. AUDIENCE TARGETING ------------------------------------------ */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">5. Hedef Kitle Stratejisi</h3>
            <p className="text-[11px] text-gray-500 mb-3">"Aileler" kitlesi tek blok degil - dijitalde ayri ayri hedeflenebilen 6 mikro-segmente bolundu. Toplam butce payi %100.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="rounded-xl border border-pink-200 bg-pink-50 p-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-sm font-bold text-pink-800">1 - Cekirdek Aileler</span>
                  <span className="text-[11px] font-bold text-pink-700 bg-pink-100 px-2 py-0.5 rounded-full">%40</span>
                </div>
                <p className="text-[11px] text-pink-700 mb-2 font-medium">Ana hedef - butcenin omurgasi</p>
                <ul className="text-[11px] text-gray-700 space-y-1">
                  <li>• 30-44 yas, ebeveynler (cocuk 4-12 yas)</li>
                  <li>• Istanbul Anadolu Yakasi %70, Avrupa Yakasi %30</li>
                  <li>• Ilgi: cocuk aktiviteleri, ailece etkinlik, hafta sonu plani, doga</li>
                  <li>• Davranis: ebeveyn Facebook gruplari, anne bloglari, cocuk icerik tuketimi</li>
                  <li>• Lookalike: festival bileti almis aileler %1-3</li>
                </ul>
              </div>
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-sm font-bold text-blue-800">2 - Cocuklu Genc Aileler</span>
                  <span className="text-[11px] font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">%15</span>
                </div>
                <p className="text-[11px] text-blue-700 mb-2 font-medium">Genisleyen pazar</p>
                <ul className="text-[11px] text-gray-700 space-y-1">
                  <li>• 25-34 yas, ilk cocuk / 0-6 yas cocuklu genc ebeveynler</li>
                  <li>• Istanbul + Kocaeli, Tekirdag</li>
                  <li>• Ilgi: cocuk urunleri, oyun gruplari, cocuk gelisimi, hafta sonu aktivitesi</li>
                  <li>• Davranis: anne-baba topluluklari, Reels/Story, cocuk icerigi</li>
                  <li>• Custom: site ziyaretcileri (lookalike base)</li>
                </ul>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-sm font-bold text-emerald-800">3 - Yakin Cevre (Lokasyon)</span>
                  <span className="text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">%12</span>
                </div>
                <p className="text-[11px] text-emerald-700 mb-2 font-medium">En yuksek niyet - dusuk CAC</p>
                <ul className="text-[11px] text-gray-700 space-y-1">
                  <li>• Kalamis/Kadikoy merkezli 0-10 km radius hedefleme</li>
                  <li>• Fenerbahce, Caddebostan, Goztepe, Suadiye, Bostanci</li>
                  <li>• "Yurume/kisa surus mesafesinde" mesaj acisi</li>
                  <li>• Davranis: lokal yer/etkinlik aramalari, Maps etkilesimi</li>
                  <li>• Faz 3-4'te (erken bilet + son cagri) agirlik artar</li>
                </ul>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-sm font-bold text-amber-800">4 - Etkinlik & Festival Tutkunlari</span>
                  <span className="text-[11px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">%10</span>
                </div>
                <p className="text-[11px] text-amber-700 mb-2 font-medium">Ilgi-bazli niyet sinyali</p>
                <ul className="text-[11px] text-gray-700 space-y-1">
                  <li>• Daha once cocuk etkinligi / festival bileti almis kullanicilar</li>
                  <li>• Ilgi: konser, fuar, tema parki, cocuk tiyatrosu, festival</li>
                  <li>• Bilet platformlari (Biletix, Passo vb.) etkilesimi</li>
                  <li>• Demand Gen + In-Market "etkinlik/eglence" segmentleri</li>
                  <li>• Lookalike base: gecmis HUPALUPA katilimcilari</li>
                </ul>
              </div>
              <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-sm font-bold text-orange-800">5 - Genis Aile & Buyukanne-Buyukbaba</span>
                  <span className="text-[11px] font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">%8</span>
                </div>
                <p className="text-[11px] text-orange-700 mb-2 font-medium">Ek bilet/grup satis potansiyeli</p>
                <ul className="text-[11px] text-gray-700 space-y-1">
                  <li>• 45-60 yas, torunlariyla hafta sonu plani yapan kusak</li>
                  <li>• "Tum aile birlikte" - 3 kusak bir arada mesaji</li>
                  <li>• Ilgi: aile, torun, hafta sonu gezisi, doga/sahil</li>
                  <li>• Grup/aile bilet paketi vurgusu (sepet buyutme)</li>
                  <li>• Agirlik Faz 3 erken bilet doneminde</li>
                </ul>
              </div>
              <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-sm font-bold text-violet-800">6 - Retargeting Pool</span>
                  <span className="text-[11px] font-bold text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full">%15</span>
                </div>
                <p className="text-[11px] text-violet-700 mb-2 font-medium">En sicak - donusum kapatma</p>
                <ul className="text-[11px] text-gray-700 space-y-1">
                  <li>• Site ziyaret edip bilet sayfasina gelen ama satin almayan</li>
                  <li>• Video %50+ izleyen ama tiklamayan (mid funnel'da kalan)</li>
                  <li>• Add-to-Cart edip checkout'u tamamlamayan (en sicak segment)</li>
                  <li>• HUPALUPA onceki etkinlik katilimcilari (CRM upload)</li>
                  <li>• Email/SMS acip tiklamayanlar (Customer Match)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* -- 6. CREATIVE / MESSAGING GUIDE ---------------------------------- */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">6. Kreatif Mesaj Rehberi</h3>
            <div className="rounded-xl border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Funnel</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hedef</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hook / Mesaj Tonlari</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CTA</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Format</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    <tr>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded">UPPER</span></td>
                      <td className="px-4 py-3 text-xs">Marka & festival farkindaligi</td>
                      <td className="px-4 py-3 text-xs text-gray-700">
                        <div className="font-semibold">"Kalamis'ta bu yaz cocuklar icin bir sey var..."</div>
                        <div className="text-gray-500 mt-0.5">Duygusal, sahil esintisi, cocuk gulusu, muzik. Bilet dahi mention edilmez.</div>
                      </td>
                      <td className="px-4 py-3 text-xs"><span className="text-gray-600">Daha Fazla Bilgi</span></td>
                      <td className="px-4 py-3 text-xs">15-30sn Reels/TikTok video, CTV spot, Spotify audio 30sn</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded">MID</span></td>
                      <td className="px-4 py-3 text-xs">Ilgi yaratma, icerik anlatma</td>
                      <td className="px-4 py-3 text-xs text-gray-700">
                        <div className="font-semibold">"3 sahne, 12 atolye, 20+ stand. Ailece bir gun."</div>
                        <div className="text-gray-500 mt-0.5">Behind the scenes, atolye on gosterimi, ebeveyn testimonial. Net fayda + program.</div>
                      </td>
                      <td className="px-4 py-3 text-xs"><span className="text-gray-600">Icerigi Kesfet</span></td>
                      <td className="px-4 py-3 text-xs">Carousel (5 kart), Story (interactive sticker), 60sn YouTube</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded">LOWER</span></td>
                      <td className="px-4 py-3 text-xs">Bilet satisi kapatma</td>
                      <td className="px-4 py-3 text-xs text-gray-700">
                        <div className="font-semibold">"Aile bilet paketi &#8378;X. Erken bilet %25 indirim. Son N gun."</div>
                        <div className="text-gray-500 mt-0.5">Net fiyat + countdown + sosyal kanit ("3.420 aile bilet aldi").</div>
                      </td>
                      <td className="px-4 py-3 text-xs"><span className="text-emerald-700 font-semibold">Bilet Al</span></td>
                      <td className="px-4 py-3 text-xs">Static + carousel urun, Search RSA, dynamic retargeting</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </section>

          {/* -- 7. KPI PROJECTIONS --------------------------------------------- */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">7. Performans Projeksiyonu</h3>
            <div className="rounded-xl border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Senaryo</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Site Trafigi</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CVR</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Bilet</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CAC</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Hedef Karsiligi</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  <tr className="bg-rose-50/50">
                    <td className="px-4 py-3 text-sm font-medium text-rose-800">Pesimist</td>
                    <td className="px-4 py-3 text-sm text-right">102.000</td>
                    <td className="px-4 py-3 text-sm text-right">2.85%</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">~2.900</td>
                    <td className="px-4 py-3 text-sm text-right">&#8378;259</td>
                    <td className="px-4 py-3 text-sm text-right text-rose-700">Hedefin altinda - kreatif/checkout sorunu, butce ek destegi</td>
                  </tr>
                  <tr className="bg-blue-50/50">
                    <td className="px-4 py-3 text-sm font-medium text-blue-800">Baseline (en olasi)</td>
                    <td className="px-4 py-3 text-sm text-right">120.000</td>
                    <td className="px-4 py-3 text-sm text-right">3.10%</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">~3.720</td>
                    <td className="px-4 py-3 text-sm text-right">&#8378;200</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-700">Hedef merkezi - 3K-4.5K bandinin ortasi</td>
                  </tr>
                  <tr className="bg-emerald-50/50">
                    <td className="px-4 py-3 text-sm font-medium text-emerald-800">Optimist</td>
                    <td className="px-4 py-3 text-sm text-right">138.000</td>
                    <td className="px-4 py-3 text-sm text-right">3.30%</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">~4.550</td>
                    <td className="px-4 py-3 text-sm text-right">&#8378;165</td>
                    <td className="px-4 py-3 text-sm text-right text-emerald-700">Hedefin ustunde - kreatif yuksek perform, viral aile icerigi</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* -- 8. CONTENT / ASSET REQUIREMENTS -------------------------------- */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">8. Icerik Hazirlik &amp; Boyut Rehberi</h3>
            <div className="rounded-xl border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hazirlanacak Icerik</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tip</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cikarilacak Boyutlar (ayni icerik, tum oranlar)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platformlar</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium align-top">Video icerik (tanitim filmi, aile vlog, BTS Reels)</td>
                      <td className="px-4 py-3 text-xs align-top"><span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">Video</span></td>
                      <td className="px-4 py-3 text-sm align-top">
                        <div className="space-y-0.5">
                          <div>9:16 - 1080 x 1920 (dikey)</div>
                          <div>1:1 - 1080 x 1080 (kare)</div>
                          <div>16:9 - 1920 x 1080 (yatay, 4K master)</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <img src="/6033716.png" alt="Meta" title="Meta" className="h-5 w-5 object-contain" />
                          <img src="/tiktokv4.svg" alt="TikTok" title="TikTok" className="h-5 w-5 object-contain" />
                          <img src="/youtubelogov2.svg" alt="YouTube / CTV" title="YouTube / CTV" className="h-4 w-auto object-contain" />
                          <img src="/google_ads-512.webp" alt="Google Ads" title="Google Ads" className="h-5 w-5 object-contain" />
                          <span className="px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-medium rounded border border-teal-100">Programmatic</span>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium align-top">Sosyal statik &amp; carousel gorsel</td>
                      <td className="px-4 py-3 text-xs align-top"><span className="px-2 py-0.5 bg-sky-100 text-sky-700 rounded">Gorsel</span></td>
                      <td className="px-4 py-3 text-sm align-top">
                        <div className="space-y-0.5">
                          <div>1:1 - 1080 x 1080 (kare)</div>
                          <div>4:5 - 1080 x 1350 (dikey)</div>
                          <div>9:16 - 1080 x 1920 (story)</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <img src="/6033716.png" alt="Meta" title="Meta (Feed, Carousel, Story, Sales)" className="h-5 w-5 object-contain" />
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium align-top">Display &amp; native gorsel</td>
                      <td className="px-4 py-3 text-xs align-top"><span className="px-2 py-0.5 bg-sky-100 text-sky-700 rounded">Gorsel</span></td>
                      <td className="px-4 py-3 text-sm align-top">
                        <div className="space-y-0.5">
                          <div>1.91:1 - 1200 x 628 (yatay)</div>
                          <div>1:1 - 1200 x 1200 (kare)</div>
                          <div>Logo - 1200 x 1200 + 1200 x 300</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <img src="/google_ads-512.webp" alt="Google Ads" title="Google Ads (PMAX, Demand Gen, GDN)" className="h-5 w-5 object-contain" />
                          <span className="px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-medium rounded border border-teal-100">Programmatic</span>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium align-top">Display banner seti (IAB)</td>
                      <td className="px-4 py-3 text-xs align-top"><span className="px-2 py-0.5 bg-sky-100 text-sky-700 rounded">Gorsel</span></td>
                      <td className="px-4 py-3 text-sm align-top">
                        <div className="space-y-0.5">
                          <div>300 x 250, 728 x 90, 160 x 600</div>
                          <div>300 x 600, 320 x 100, 970 x 250</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <img src="/google_ads-512.webp" alt="Google Ads" title="Google Ads (GDN)" className="h-5 w-5 object-contain" />
                          <span className="px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-medium rounded border border-teal-100">Programmatic</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800 leading-relaxed">
              <span className="font-semibold">Onemli:</span> Ayni icerik birden fazla boyutta hazirlanir - <span className="font-semibold">farkli icerik degildir</span>. Bir video konsepti cekildiginde 9:16, 1:1 ve 16:9 olarak disa aktarilir; tek master (16:9 / 4K) cekilip diger oranlara kirpilir. Hedef produksiyon: <span className="font-semibold">4 video konsepti + 8 statik + 3 carousel</span>. Teslim: lansman oncesi (kampanya 1 Haziran baslar). Video: MP4 / H.264. Gorsel: JPG / PNG, sRGB.
            </div>
          </section>

        </div>
      </div>

      </div>
    </div>
  );
}
